import path from 'path';
import fs from 'fs-extra';
import matter from 'gray-matter';
import { KnowledgeEntry, KnowledgeType } from '../types';
import { logger } from '../utils/logger';
import { RipgrepSearch } from '../utils/ripgrep';
import { WORKFLOW_DIR, KNOWLEDGE_DIR } from '../constants';

export interface SearchOptions {
  type?: KnowledgeType;
  tags?: string[];
  limit?: number;
}

export interface SearchResult {
  id: string;
  type: KnowledgeType;
  title: string;
  path: string;
  preview: string;
  score: number;
}

export class KnowledgeBase {
  private workflowDir: string;
  private knowledgeDir: string;
  private ripgrep: RipgrepSearch;

  constructor(projectRoot: string = process.cwd()) {
    this.workflowDir = path.join(projectRoot, WORKFLOW_DIR);
    this.knowledgeDir = path.join(this.workflowDir, KNOWLEDGE_DIR);
    this.ripgrep = new RipgrepSearch();
  }

  async search(term: string, options: SearchOptions = {}): Promise<SearchResult[]> {
    // Try using ripgrep first for better performance
    const ripgrepResults = await this.ripgrep.search({
      pattern: term,
      path: this.knowledgeDir,
      fileType: 'markdown',
      maxCount: 100,
      ignoreCase: true
    });

    if (ripgrepResults.length > 0) {
      return this.processRipgrepResults(ripgrepResults, term, options);
    }

    // Fallback to manual search if ripgrep is not available
    return this.manualSearch(term, options);
  }

  private async processRipgrepResults(
    ripgrepResults: any[],
    term: string,
    options: SearchOptions
  ): Promise<SearchResult[]> {
    const results: SearchResult[] = [];
    const processedFiles = new Set<string>();

    for (const result of ripgrepResults) {
      if (processedFiles.has(result.file)) continue;
      processedFiles.add(result.file);

      const content = await fs.readFile(result.file, 'utf-8');
      const { data, content: body } = matter(content);
      
      // Extract type from path
      const relativePath = path.relative(this.knowledgeDir, result.file);
      const typeMatch = relativePath.match(/^(\w+)s\//);
      const type = typeMatch ? typeMatch[1] as KnowledgeType : 'snippet';

      if (options.type && type !== options.type) continue;

      results.push({
        id: path.basename(result.file, '.md'),
        type,
        title: data.title || path.basename(result.file),
        path: relativePath,
        preview: result.context || this.extractPreview(body, term),
        score: this.calculateScore(data.title || '', body, term, data.tags || [])
      });
    }

    return results
      .sort((a, b) => b.score - a.score)
      .slice(0, options.limit || 10);
  }

  private async manualSearch(term: string, options: SearchOptions): Promise<SearchResult[]> {
    const results: SearchResult[] = [];
    const searchTerm = term.toLowerCase();
    
    const types: KnowledgeType[] = options.type 
      ? [options.type] 
      : ['pattern', 'decision', 'troubleshooting', 'snippet'];
    
    for (const type of types) {
      const typeDir = path.join(this.knowledgeDir, `${type}s`);
      
      if (!await fs.pathExists(typeDir)) continue;
      
      const files = await fs.readdir(typeDir);
      
      for (const file of files) {
        if (!file.endsWith('.md')) continue;
        
        const filePath = path.join(typeDir, file);
        const content = await fs.readFile(filePath, 'utf-8');
        const { data, content: body } = matter(content);
        
        const score = this.calculateScore(
          data.title || file,
          body,
          searchTerm,
          data.tags || []
        );
        
        if (score > 0) {
          results.push({
            id: path.basename(file, '.md'),
            type,
            title: data.title || file,
            path: path.relative(this.knowledgeDir, filePath),
            preview: this.extractPreview(body, searchTerm),
            score
          });
        }
      }
    }
    
    return results
      .sort((a, b) => b.score - a.score)
      .slice(0, options.limit || 10);
  }

  private calculateScore(title: string, body: string, term: string, tags: string[]): number {
    let score = 0;
    const searchTerm = term.toLowerCase();
    const titleLower = title.toLowerCase();
    const bodyLower = body.toLowerCase();
    
    // Title matches
    if (titleLower.includes(searchTerm)) {
      score += titleLower === searchTerm ? 10 : 5;
    }
    
    // Body matches
    const bodyMatches = (bodyLower.match(new RegExp(searchTerm, 'g')) || []).length;
    score += Math.min(bodyMatches, 5);
    
    // Tag matches
    tags.forEach((tag: string) => {
      if (tag.toLowerCase().includes(searchTerm)) {
        score += 3;
      }
    });
    
    return score;
  }

  async add(entry: Omit<KnowledgeEntry, 'id' | 'createdAt' | 'updatedAt'>): Promise<KnowledgeEntry> {
    const id = this.generateId(entry.title);
    const now = new Date();
    
    const fullEntry: KnowledgeEntry = {
      ...entry,
      id,
      createdAt: now,
      updatedAt: now
    };
    
    // Prepare content with frontmatter
    const content = matter.stringify(entry.content || '', {
      title: entry.title,
      type: entry.type,
      tags: entry.tags,
      createdAt: now.toISOString(),
      updatedAt: now.toISOString(),
      ...entry.metadata
    });
    
    // Write file
    const dir = path.join(this.knowledgeDir, `${entry.type}s`);
    await fs.ensureDir(dir);
    
    const filePath = path.join(dir, `${id}.md`);
    await fs.writeFile(filePath, content);
    
    logger.debug(`Added knowledge entry: ${filePath}`);
    
    return fullEntry;
  }

  async get(type: KnowledgeType, id: string): Promise<KnowledgeEntry | null> {
    const filePath = path.join(this.knowledgeDir, `${type}s`, `${id}.md`);
    
    if (!await fs.pathExists(filePath)) {
      return null;
    }
    
    const content = await fs.readFile(filePath, 'utf-8');
    const { data, content: body } = matter(content);
    
    return {
      id,
      type,
      title: data.title || id,
      content: body,
      tags: data.tags || [],
      createdAt: new Date(data.createdAt),
      updatedAt: new Date(data.updatedAt),
      metadata: data
    };
  }

  async list(options: { type?: KnowledgeType; limit?: number } = {}): Promise<KnowledgeEntry[]> {
    const entries: KnowledgeEntry[] = [];
    
    const types: KnowledgeType[] = options.type 
      ? [options.type] 
      : ['pattern', 'decision', 'troubleshooting', 'snippet'];
    
    for (const type of types) {
      const typeDir = path.join(this.knowledgeDir, `${type}s`);
      
      if (!await fs.pathExists(typeDir)) {
        continue;
      }
      
      const files = await fs.readdir(typeDir);
      
      for (const file of files) {
        if (!file.endsWith('.md')) continue;
        
        const entry = await this.get(type, path.basename(file, '.md'));
        if (entry) {
          entries.push(entry);
        }
      }
    }
    
    // Sort by updated date and apply limit
    return entries
      .sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime())
      .slice(0, options.limit || 100);
  }

  private extractPreview(content: string, searchTerm: string): string {
    const lines = content.split('\n').filter(line => line.trim());
    const termLower = searchTerm.toLowerCase();
    
    // Find first line containing search term
    const matchingLine = lines.find(line => 
      line.toLowerCase().includes(termLower)
    );
    
    if (matchingLine) {
      return matchingLine.trim();
    }
    
    // Otherwise return first non-empty line
    return lines[0] || '';
  }

  private generateId(title: string): string {
    const base = title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    
    const timestamp = Date.now().toString(36);
    return `${base}-${timestamp}`;
  }
}